import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { Link } from "@/components/_molecules/navigation-controls/link";
import { APIError } from "@/services/errors.service";

import { verify2FAAndLogin } from "../services/auth.service";

interface TwoFactorVerificationFormProps {
  twoFactorToken: string;
  onSuccess: () => void;
  onBack: () => void;
}

export function TwoFactorVerificationForm({ twoFactorToken, onSuccess, onBack }: TwoFactorVerificationFormProps) {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const { t } = useTranslation("auth");

  const submitCode = async () => {
    if (loading) return;
    const isValidLength = useBackupCode ? code.length === 10 : code.length === 6;
    if (!isValidLength) return;

    setLoading(true);
    setErrors([]);
    try {
      await verify2FAAndLogin(twoFactorToken, code);
      onSuccess();
    } catch (e) {
      setErrorMessages(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await submitCode();
  };

  const setErrorMessages = (error: unknown) => {
    if (error instanceof APIError) {
      setErrors(error.toArray());
    } else if (error instanceof Error) {
      const graphqlError = error as Error & {
        response?: { errors?: Array<{ message?: string }>; status?: number };
      };
      if (graphqlError.response?.errors?.[0]?.message) {
        const message = graphqlError.response.errors[0].message;
        if (message.includes("Invalid verification code") || message.includes("verification code")) {
          setErrors([t("twoFactor.errors.invalidCodeVerify")]);
        } else {
          setErrors([t("twoFactor.errors.generic")]);
        }
      } else {
        if (error.message.includes("Invalid verification code") || error.message.includes("verification code")) {
          setErrors([t("twoFactor.errors.invalidCodeVerify")]);
        } else {
          setErrors([t("twoFactor.errors.generic")]);
        }
      }
    } else {
      setErrors([t("twoFactor.errors.invalidCodeVerify")]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 bg-background px-4 py-8 sm:px-16">
      <div>
        <h2 className="mb-2 text-lg font-semibold">{t("twoFactor.verify.title")}</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {useBackupCode
            ? t("twoFactor.verify.enterBackupCode")
            : t("twoFactor.verify.enterCode")}
        </p>
      </div>

      <div>
        <label htmlFor="code" className="block text-xs text-foreground">
          {useBackupCode ? t("twoFactor.verify.backupCodeLabel") : t("twoFactor.verify.verificationCodeLabel")}
        </label>
        <input
          id="code"
          name="code"
          type="text"
          inputMode={useBackupCode ? "text" : "numeric"}
          autoCapitalize={useBackupCode ? "characters" : undefined}
          pattern={useBackupCode ? "[0-9A-Z]{10}" : "[0-9]{6}"}
          maxLength={useBackupCode ? 10 : 6}
          placeholder={useBackupCode ? "XXXXXXXXXX" : "000000"}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => {
            const value = useBackupCode
              ? e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "")
              : e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
            setCode(value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitCode();
            }
          }}
          className="mt-1 block w-full appearance-none rounded-md border bg-transparent px-3 py-2 shadow-xs placeholder:text-muted-foreground focus:border-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus sm:text-sm"
        />
      </div>

      <div className="flex items-center">
        <input
          id="useBackupCode"
          name="useBackupCode"
          type="checkbox"
          checked={useBackupCode}
          onChange={(e) => {
            setUseBackupCode(e.target.checked);
            setCode("");
            setErrors([]);
          }}
          className="h-4 w-4 rounded-sm border-slate-300 text-primary focus:ring-primary"
        />
        <label htmlFor="useBackupCode" className="ml-2 block text-xs text-foreground">
          {t("twoFactor.verify.useBackupCode")}
        </label>
      </div>

      <div className="text-sm text-destructive">
        {errors.length > 0 && (
          <ul className="list-none">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex space-x-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={loading} className="w-full">
          {t("twoFactor.verify.back")}
        </Button>
        <Button
          type="submit"
          disabled={loading || (useBackupCode ? code.length !== 10 : code.length !== 6)}
          className="w-full"
        >
          {loading ? <LoadingDots color="#808080" /> : <p>{t("twoFactor.verify.confirm")}</p>}
        </Button>
      </div>

      <div className="text-center">
        <Link to="/forgot-2fa" className="text-xs text-muted-foreground underline hover:text-foreground">
          {t("twoFactor.verify.noAccess")}
        </Link>
      </div>
    </form>
  );
}
